import { defineStore } from 'pinia'
import { roles, type Terms } from '~/enums'

function createProgressKey({
  schoolId,
  userId,
  term,
  grade,
  subject,
  gameId,
  level,
}: GameProgressKey): string {
  return `${schoolId}_${userId}_${subject}_${grade}_${term}_${gameId}_${level}`
}

const LEVEL_COMPLETED = 100

export const useGameProgressStore = defineStore('gameProgressStore', {
  state: () => ({
    // A normalized map of game progress records.
    // Each record is of type GameProgressRecord (which extends GameProgress).
    progress: {} as Record<string, GameProgressRecord>,
    syncTimer: null as ReturnType<typeof setInterval> | null,
  }),
  getters: {
    // Get all progress records for a specific user, grade, subject, and term.
    getProgressByTerm:
      (state) =>
      (
        userId: string,
        schoolId: string,
        subject: string,
        grade: string,
        term: Terms,
      ): GameProgressRecord[] => {
        return Object.values(state.progress).filter(
          (record) =>
            record.userId === userId &&
            record.schoolId === schoolId &&
            record.subject === subject &&
            record.grade === grade &&
            record.term === term &&
            true,
        )
      },
    // Get a specific progress record by gameId and level.
    getProgressByGameAndLevel:
      (state) =>
      (
        userId: string,
        schoolId: string,
        subject: string,
        grade: string,
        term: Terms,
        gameId: string,
        level: number,
      ): GameProgressRecord | undefined => {
        const key = createProgressKey({
          schoolId,
          userId,
          subject,
          grade,
          term,
          gameId,
          level,
        })
        return state.progress[key]
      },
    getIsUnlockedByGameAndLevel:
      (state) =>
      (
        userId: string,
        schoolId: string,
        subject: string,
        grade: string,
        term: Terms,
        gameId: string,
        level: number,
      ): boolean => {
        const key = createProgressKey({
          schoolId,
          userId,
          subject,
          grade,
          term,
          gameId,
          level,
        })
        return state.progress[key]?.unlocked ?? false
      },
  },
  actions: {
    async getOnlineGameProgress(
      userId: string,
      schoolId: string,
      subject: string,
      grade: string,
      term: Terms,
    ) {
      if (!userId || !schoolId) return

      const { data } = await $fetch<{ data: GameProgressRecord[] }>(
        '/api/gameProgress/gameProgress',
        {
          method: 'GET',
          params: { userId, schoolId, subject, grade, term },
        },
      )
      const records = data

      records?.forEach((record) => {
        record.schoolId =
          (record as GameProgressRecord & { tenantId?: string }).tenantId ??
          schoolId
        // Create the composite key.
        const key = createProgressKey({
          schoolId: record.schoolId,
          userId: record.userId,
          subject,
          grade,
          term: record.term as Terms,
          gameId: record.gameId,
          level: record.level,
        })
        // Only update the local values when the online record has a higher levelCompletion value.
        if (
          !this.progress[key] ||
          record.levelCompletion > this.progress[key]?.levelCompletion
        ) {
          this.progress[key] = record
          if (record.levelCompletion > LEVEL_COMPLETED) {
            this.unlockNextLevel(record)
          }
        }
      })
    },
    // Increase game score and update progress.
    async updateProgress(
      gameId: string,
      userId: string,
      schoolId: string,
      subject: string,
      grade: string,
      term: Terms,
      strand: string,
      substrand: string,
      curriculumWeek: number | undefined,
      level: number,
      levelCompletion: number,
      unlocked: boolean,
      isSynced: boolean,
      isCorrect: boolean,
      timeTaken: number,
    ) {
      const user = useSupabaseUser()
      const currentRoleStore = useCurrentRoleStore()
      if (
        currentRoleStore.currentRole?.role !== roles.STUDENT ||
        user.value?.id !== userId
      )
        return

      const key = createProgressKey({
        schoolId,
        userId,
        subject,
        grade,
        term,
        gameId,
        level,
      })
      let record = this.progress[key]
      if (!record) {
        // Create a new record if one doesn't exist.
        record = {
          userId,
          schoolId,
          subject,
          grade,
          term,
          gameId,
          level,
          levelCompletion: Math.min(LEVEL_COMPLETED, levelCompletion),
          correct: isCorrect ? 1 : 0,
          incorrect: isCorrect ? 0 : 1,
          avgTimeTaken: timeTaken,
          unlocked,
          strand,
          substrand,
          curriculumWeek,
          isSynced,
        } as GameProgressRecord
        this.progress[key] = record
      } else {
        record.levelCompletion = Math.min(
          LEVEL_COMPLETED,
          record.levelCompletion + levelCompletion,
        )
        // Update average time taken.
        const answeredQuestions = record.correct + record.incorrect
        record.avgTimeTaken = Math.round(
          (record.avgTimeTaken * answeredQuestions + timeTaken) /
            (answeredQuestions + 1),
        )
        record.correct += isCorrect ? 1 : 0
        record.incorrect += isCorrect ? 0 : 1
        record.isSynced = false
      }
      await this.syncUnsyncedProgress()
      // Unlock next level if threshold reached.
      if (record.levelCompletion >= LEVEL_COMPLETED) {
        this.unlockNextLevel(record)
      }
    },
    async syncUnsyncedProgress() {
      const user = useSupabaseUser()
      const currentRoleStore = useCurrentRoleStore()
      const activeUserId = user.value?.id

      // Progress can only be written by a student for their own active class.
      // Discard stale local writes made while previewing games as staff so they
      // are not retried every ten seconds after the server rejects them.
      if (currentRoleStore.currentRole?.role !== roles.STUDENT) {
        Object.entries(this.progress).forEach(([key, record]) => {
          if (!record.isSynced) Reflect.deleteProperty(this.progress, key)
        })
        return
      }
      if (!activeUserId) return

      Object.entries(this.progress).forEach(([key, record]) => {
        if (!record.isSynced && record.userId !== activeUserId) {
          Reflect.deleteProperty(this.progress, key)
        }
      })

      // Gather all unsynced records.
      const unsyncedRecords = Object.values(this.progress).filter(
        (record) =>
          record.isSynced === false &&
          record.userId === activeUserId &&
          Boolean(record.schoolId),
      )
      if (!unsyncedRecords.length) return
      // Create an array of promises to sync each record in parallel.
      const syncPromises = unsyncedRecords.map((record) => {
        return $fetch('/api/gameProgress/gameProgress', {
          method: 'PUT',
          body: record,
        })
          .then(() => {
            // Mark the record as synced.
            record.isSynced = true
          })
          .catch((error) => {
            console.error('Sync failed for record', record, error)
            // Leave record unsynced for future attempts.
          })
      })

      // Run all sync operations in parallel.
      await Promise.all(syncPromises)
    },
    // Start a timer to periodically check for unsynced progress.
    startSyncTimer() {
      if (this.syncTimer) return
      this.syncTimer = setInterval(() => {
        if (navigator.onLine) {
          this.syncUnsyncedProgress()
        }
      }, 10000)
    },
    // Stop the sync timer if needed.
    stopSyncTimer() {
      if (this.syncTimer) {
        clearInterval(this.syncTimer)
        this.syncTimer = null
      }
    },
    clearProgress() {
      this.stopSyncTimer()
      this.progress = {}
    },
    unlockNextLevel(record: GameProgressRecord) {
      const nextKey = createProgressKey({
        schoolId: record.schoolId,
        userId: record.userId,
        grade: record.grade,
        subject: record.subject,
        term: record.term,
        gameId: record.gameId,
        level: record.level + 1,
      })
      if (!this.progress[nextKey]) {
        this.progress[nextKey] = {
          userId: record.userId,
          schoolId: record.schoolId,
          subject: record.subject,
          grade: record.grade,
          term: record.term,
          gameId: record.gameId,
          level: record.level + 1,
          levelCompletion: 0,
          correct: 0,
          incorrect: 0,
          avgTimeTaken: 0,
          unlocked: true,
          strand: record.strand,
          substrand: record.substrand,
          curriculumWeek: record.curriculumWeek,
          isSynced: false,
        } as GameProgressRecord
      }
    },
  },
})
